import { filterRecommendationProducts } from 'lib/utils/recommendation-eligibility'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import React, { useCallback, useEffect, useState } from 'react'
import Typo from 'constants/typography'
import { withI18next } from 'lib/i18n/withI18next'
import { hasLocizeTranslation } from 'lib/utils/locize'
import InfiniteScroll from 'react-infinite-scroll-component'
import CategoryProductList from 'pages/category/list'
import Loader from 'components/loader'
import usePersonalizedRecommendation from 'components/shared/useEffect/usePersonalizedRecommendation'
import ProductListSkeleton from 'components/product/product-list-skeleton'
import GridViewButtons from 'components/shared/category/mobile-sort-filter/wrappers/grid-view-buttons/GridViewButtons'
import BackToTopCTA from 'components/shared/back-to-top-cta'
import { getProductIDs } from 'lib/utils/handle-response'
import { segmentTrackProductListViewed } from 'lib/segment'
import { SEGMENT_REF_TYPES } from 'lib/segment/const'
import Button from 'components/common/button'
import styles from './styles.scss'
import { fetchJustForYouProducts } from './utils'

const JustForYouContainer = ({
  isShowTitle,
  auth,
  isPhone,
  productId,
  refType,
  t,
  isFromHomePage,
  refId,
}) => {
  const [personalizedProduct, isFetched] = usePersonalizedRecommendation(
    auth,
    productId,
    false,
    18,
    0,
  )
  const [pageNo, setPageNo] = useState(0)
  const [skipProduct, setSkipProduct] = useState(0)
  const [productLists, setProductLists] = useState([])
  const [hasMoreProducts, setHasMoreProducts] = useState(true)

  const setJustForYouProducts = useCallback(
    (products) => {
      setProductLists([...productLists, ...products])
      setHasMoreProducts(
        productLists?.length < productLists.length + products.length,
      )
      setSkipProduct(skipProduct + products.length)
      setPageNo(pageNo + 1)
    },
    [pageNo, productLists, skipProduct],
  )

  // Keep raw counts for server offsets, even when some cards are excluded.
  const visibleProducts = filterRecommendationProducts(productLists)

  const loadMoreProducts = () =>
    fetchJustForYouProducts({
      auth,
      productId,
      pageNo,
      refId: refId || productId || '',
      refType: refType || 'pdp_just_for_you',
      skipProduct,
      onSuccess: (res) => setJustForYouProducts(res),
      onError: () => setHasMoreProducts(false),
    })

  useEffect(() => {
    if (personalizedProduct?.products?.length && !productLists.length) {
      setJustForYouProducts(personalizedProduct?.products)

      if (refType === SEGMENT_REF_TYPES.APP_FEED && isFromHomePage) {
        // Note: Trigger product list view event here instead of relying on componentDidMount inside the CategoryProductList
        const productIds = getProductIDs(
          filterRecommendationProducts(personalizedProduct?.products),
        )

        segmentTrackProductListViewed(
          {
            id_site_category: 111,
            page_number: 1,
            product_list_ids: productIds,
            sort_by: 'popular',
          },
          {
            ref_type: SEGMENT_REF_TYPES.APP_FEED,
            ref_id: refId,
          },
        )
      }
    }
  }, [
    personalizedProduct,
    productLists,
    setJustForYouProducts,
    refId,
    refType,
    isFromHomePage,
  ])

  if (!isFetched) {
    return (
      <div className="jfy__container">
        <div className="jfy__loader">
          <style jsx>{styles}</style>
          <ProductListSkeleton number={40} />
        </div>
      </div>
    )
  }

  return (
    <div className="jfy__container">
      <style jsx>{styles}</style>
      {isShowTitle && (
        <div className={Typo.h6}>
          <span className="jfy__title">
            {hasLocizeTranslation(
              t,
              'JUST_FOR_YOU_PRODUCTS_TITLE',
              'Just For You',
            )}
          </span>
        </div>
      )}
      {isPhone && (
        <div
          className={clsx('jfy__grid', {
            jfy__grid__home: isFromHomePage,
          })}
        >
          <GridViewButtons refId={refId} refType={refType} />
        </div>
      )}
      <section className="similar__body">
        <InfiniteScroll
          next={!isFromHomePage && loadMoreProducts}
          dataLength={productLists.length}
          scrollThreshold={0.8}
          hasChildren={productLists.length > 0}
          hasMore={hasMoreProducts}
          loader={
            !isFromHomePage && (
              <div className="loader-container">
                <Loader />
              </div>
            )
          }
        >
          <CategoryProductList
            workwearGrid={isFromHomePage}
            category={{ id_category: 111 }}
            displayWishlistIcon
            isPhone={isPhone}
            displayQuickAddToBag
            displayHoverInteractions
            list={visibleProducts}
            refId={refId || productId || ''}
            refType={refType || 'pdp_just_for_you'}
          />
          {hasMoreProducts && (isFromHomePage || !visibleProducts.length) && (
            <Button
              className="jfy__container__load-more-cta"
              onClick={loadMoreProducts}
            >
              {hasLocizeTranslation(t, 'Load More', 'Load More')}
            </Button>
          )}
        </InfiniteScroll>
      </section>
      <BackToTopCTA showBackToTop />
    </div>
  )
}

JustForYouContainer.defaultProps = {
  refId: null,
  refType: '',
  isShowTitle: true,
  isFromHomePage: false,
  productId: null,
}

JustForYouContainer.propTypes = {
  refId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  refType: PropTypes.string,
  isShowTitle: PropTypes.bool,
  t: PropTypes.func.isRequired,
  isFromHomePage: PropTypes.bool,
  isPhone: PropTypes.bool.isRequired,
  auth: PropTypes.shape({}).isRequired,
  productId: PropTypes.number,
}

export default withI18next()(JustForYouContainer)
